import { InfoOutlined } from '@mui/icons-material';
import {
  Avatar,
  Grid,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  styled,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { IComposableDashboardCollectionsContext, IComposableDashboardContext } from 'wisestep-api';
import {
  WsColors,
  WsButton,
  WsElementWithPopOver,
  WsIconMoreVer,
  WsIconText,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';

import Loader from '../../../features/conversationWidget/components/Loader';
import {
  ComposableAnalyticsDataAccessEnum,
  useComposableAnalyticsDataAccessHook,
} from '../../../hooks/useComposableAnalyticsDataAccessHook';
import NoDashboards from '../analyticsOptions/NoDashboards';
import { useComposableDashboardCollectionsContext } from '../hooks/useComposableDashboardCollectionsContext';
import { useComposableDashboardsContext } from '../hooks/useComposableDashboardsContext';
import { IAnalyticsMenuOption } from '../types';

export const AnalyticsMenuItem = styled(ListItem)(({ theme }) => ({
  cursor: 'pointer',
  height: '100%',
  minHeight: 80,
  border: '2px solid' + WsColors.BorderGrey,
  borderRadius: theme.spacing(0.5),
  ':hover': {
    boxShadow: '1px 1px 5px' + WsColors.BorderGrey,
  },
}));

export interface IAnalyticsContentLayoutProps {
  options: IAnalyticsMenuOption[];
  title: string;
  headerButton?: ReactElement;
  setSelectedDashboardId?: 'master' | 'dashboard';
  access?: boolean;
}

const AnalyticsContentLayout = ({
  options,
  title,
  headerButton,
  setSelectedDashboardId,
}: IAnalyticsContentLayoutProps) => {
  const navigate = useNavigate();
  const { setSelectedDashboardByIndex, dashboards, loader } =
    useComposableDashboardsContext() as Required<IComposableDashboardContext>;

  const { setSelectedCollectionByIndex, dashboardCollections } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;

  const { access: hasDataAccess, owns: isAdmin } = useComposableAnalyticsDataAccessHook([
    ComposableAnalyticsDataAccessEnum.CREATE_DASHBOARDS,
    ComposableAnalyticsDataAccessEnum.CREATE_MASTER_DASHBOARDS,
  ]);
  const onNavigate = (index: number, option: IAnalyticsMenuOption) => {
    if (!isEmpty(option.url)) {
      setSelectedDashboardId == 'master' && setSelectedCollectionByIndex(index, true);
      setSelectedDashboardId == 'dashboard' && setSelectedDashboardByIndex(index);
      navigate(option.url);
    }
  };
  const dashboardTitles = ['Your Dashboards', 'Master Dashboards'];
  const dataAccess =
    (setSelectedDashboardId == 'master' ? hasDataAccess?.[1] : hasDataAccess?.[0]) || isAdmin;

  const getShortOptionDesc = (optionDesc: string) => {
    const desc = optionDesc.slice(0, 40);
    return 'Description: ' + desc + '...';
  };

  const renderAnalyticsMenuItems = () => {
    return options.map((option, i) => (
      <Grid key={i} item xs={6}>
        <AnalyticsMenuItem
          onClick={() => {
            option?.openInNewTab ? window.open(option.url, '_blank') : onNavigate(i, option);
          }}
          secondaryAction={
            <Stack direction="row">
              {option.description && (
                <Tooltip title={option.description}>
                  <InfoOutlined color="disabled" />
                </Tooltip>
              )}
              {option.showKebabMenu && (
                <WsElementWithPopOver element={<WsIconMoreVer />}>
                  <Stack p={1}>
                    {option.kebabMenuOptions?.map((item) => (
                      <ListItemButton
                        key={item.text}
                        onClick={(event) => {
                          item?.onClick(i);
                          event.stopPropagation();
                        }}
                      >
                        <WsIconText
                          text={item.text}
                          icon={<></>}
                          textSize={WsTypoVariants.BODY1}
                          textColor={WsTypoColors.PRIMARY}
                          textCursor="pointer"
                        />
                      </ListItemButton>
                    ))}
                  </Stack>
                </WsElementWithPopOver>
              )}
            </Stack>
          }
        >
          {option.icon && (
            <ListItemAvatar>
              <Avatar>{option.icon}</Avatar>
            </ListItemAvatar>
          )}
          <ListItemText
            primaryTypographyProps={{ fontWeight: 700 }}
            primary={option.label}
            secondary={
              <Stack direction="column">
                <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.BLACK}>
                  {option.caption}
                </WsTypography>
                <WsTypography variant={WsTypoVariants.CAPTION} color={WsTypoColors.BLACK}>
                  {setSelectedDashboardId == 'master'
                    ? ''
                    : getShortOptionDesc(option.description ?? '')}
                </WsTypography>
              </Stack>
            }
          />
        </AnalyticsMenuItem>
      </Grid>
    ));
  };
  return (
    <Grid container rowGap={2} columnSpacing={2}>
      {dataAccess && dashboardTitles.includes(title) && (
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            {headerButton ?? (
              <WsButton
                onClick={() => {
                  setSelectedDashboardByIndex(-1);
                  navigate('/workspace-users-and-teams/your-dashboards/view');
                }}
              >
                Create new Dashboard
              </WsButton>
            )}
          </Stack>
        </Grid>
      )}
      {setSelectedDashboardId == 'dashboard' ? (
        isEmpty(dashboards) && loader ? (
          <Loader />
        ) : isEmpty(dashboards) ? (
          <NoDashboards />
        ) : (
          renderAnalyticsMenuItems()
        )
      ) : setSelectedDashboardId == 'master' ? (
        isEmpty(dashboardCollections) && loader ? (
          <Loader />
        ) : isEmpty(dashboardCollections) ? (
          <NoDashboards />
        ) : (
          renderAnalyticsMenuItems()
        )
      ) : (
        renderAnalyticsMenuItems()
      )}
    </Grid>
  );
};
export default AnalyticsContentLayout;
